import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { apiClient } from '../../common/apiClient'
import {
  announcePolicyStatus,
  OPEN_POLICY_READER,
  POLICY_READER_PARAM,
} from '../../common/policyReader'
import { PolicyReadModal } from './PolicyReadModal'
import { useUserPermissions } from '../../contexts/UserPermissionsContext'

/**
 * The one policy reader. Any page opens it with openPolicyReader(id) or a
 * `?policy=<id>` link, reads and confirms in place, and never navigates away.
 */
export function PolicyReaderHost() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isOverride, effectiveUserEmail } = useUserPermissions()
  const [policyId, setPolicyId] = useState<number | null>(null)
  const [open, setOpen] = useState(false)
  // Each open gets a fresh reader, even for the same policy.
  const [session, setSession] = useState(0)

  const show = useCallback((id: number) => {
    if (!Number.isSafeInteger(id) || id < 1) return
    setPolicyId(id)
    setSession((current) => current + 1)
    setOpen(true)
  }, [])

  useEffect(() => {
    const onOpen = (event: Event) => show(Number((event as CustomEvent<number>).detail))
    window.addEventListener(OPEN_POLICY_READER, onOpen)
    return () => window.removeEventListener(OPEN_POLICY_READER, onOpen)
  }, [show])

  const linkedId = new URLSearchParams(location.search).get(POLICY_READER_PARAM)
  useEffect(() => {
    if (linkedId) show(Number(linkedId))
  }, [linkedId, show])

  const close = useCallback(() => {
    setOpen(false)
    // Drop the link's ?policy= so closing, refreshing or going back doesn't reopen it.
    const params = new URLSearchParams(location.search)
    if (!params.has(POLICY_READER_PARAM)) return
    params.delete(POLICY_READER_PARAM)
    const search = params.toString()
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state }
    )
  }, [location, navigate])

  const undo = async (id: number) => {
    try {
      const { reverted } = await apiClient.post<{ reverted: number[] }>(
        '/api/policies/unacknowledge-batch',
        { policyIds: [id] }
      )
      if (reverted.length) {
        announcePolicyStatus(reverted, 'pending')
        toast.success('Undone')
      } else {
        toast.error('Too late to undo — already recorded')
      }
    } catch {
      toast.error('Failed to undo')
    }
  }

  const confirmed = (id: number) => {
    close()
    announcePolicyStatus([id], 'read')
    toast.success('Policy confirmed', {
      action: { label: 'Undo', onClick: () => void undo(id) },
      duration: 60000,
    })
  }

  if (policyId === null) return null
  return (
    <PolicyReadModal
      key={session}
      policyId={policyId}
      viewAs={isOverride ? effectiveUserEmail : undefined}
      open={open}
      onClose={close}
      onConfirmed={confirmed}
    />
  )
}
